import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { validatePassword } from "firebase/auth";
import {
  fetchBookingData,
  fetchDayAvailability,
  formatFirebaseError,
  registerWebCustomer,
  submitWebBooking,
  warmup,
} from "../api";
import { auth } from "../firebase";
import { detectLocale, t, type Locale } from "../i18n";
import { LegalFooter } from "../LegalFooter";
import {
  buildSlots,
  formatPrice,
  formatSlotDate,
  formatSlotTime,
  nextDays,
} from "../slots";
import type {
  BookingData,
  BookingDraft,
  BookingEmployee,
  BookingService,
  DayAvailability,
} from "../types";
import { applySalonBranding } from "../branding";

type Step = 0 | 1 | 2 | 3 | 4;

function availabilityCacheKey(
  dateKey: string,
  employeeId: string | null | undefined,
  noPreference: boolean
): string {
  return `${dateKey}|${noPreference ? "any" : employeeId ?? "any"}`;
}

const emptyDraft = (): BookingDraft => ({
  serviceId: null,
  employeeId: null,
  noPreference: false,
  dateKey: null,
  slotStart: null,
  guestName: "",
  guestPhone: "",
  guestEmail: "",
  note: "",
  createAccount: false,
  accountPassword: "",
  acceptConsent: false,
});

export function BookingPage() {
  const { code: salonCode = "" } = useParams();
  const [locale] = useState<Locale>(detectLocale());
  const [data, setData] = useState<BookingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<Step>(0);
  const [draft, setDraft] = useState<BookingDraft>(emptyDraft);
  const [availability, setAvailability] = useState<DayAvailability | null>(null);
  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [appointmentId, setAppointmentId] = useState<string | null>(null);
  const availabilityCache = useRef(new Map<string, DayAvailability>());

  const days = useMemo(() => nextDays(21), []);

  useEffect(() => {
    applySalonBranding(data?.branding ?? null);
  }, [data?.branding]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const result = await fetchBookingData(salonCode);
        if (!cancelled) setData(result);
      } catch {
        if (!cancelled) setError(t(locale, "salonNotFound"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [salonCode, locale]);

  const service = useMemo(
    () => data?.services.find((s) => s.id === draft.serviceId) ?? null,
    [data, draft.serviceId]
  );

  const selectedEmployee = useMemo(
    () => data?.employees.find((e) => e.id === draft.employeeId) ?? null,
    [data, draft.employeeId]
  );

  const bookableEmployees = useMemo(() => {
    if (!data || !service) return [];
    let staff = data.employees;
    if (service.bookableByEmployeeIds.length > 0) {
      staff = staff.filter((e) => service.bookableByEmployeeIds.includes(e.id));
    }
    return staff;
  }, [data, service]);

  useEffect(() => {
    if (step === 2 || step === 3) warmup("createWebBooking");
  }, [step]);

  // Prefetch first day once booking data is ready and user reaches staff/time steps.
  // Warms the Cloud Function instance and primes the cache for the common "any" case.
  useEffect(() => {
    if (!data || !service || (step !== 1 && step !== 2)) return;
    const firstDay = days[0];
    if (!firstDay) return;
    const employeeId = step === 2 && !draft.noPreference ? draft.employeeId : null;
    const noPreference = step === 1 || draft.noPreference;
    const key = availabilityCacheKey(firstDay, employeeId, noPreference);
    if (availabilityCache.current.has(key)) return;
    let cancelled = false;
    (async () => {
      try {
        const result = await fetchDayAvailability(salonCode, firstDay, employeeId);
        if (cancelled) return;
        availabilityCache.current.set(key, result);
      } catch {
        // Prefetch is best-effort; ignore failures.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [
    data,
    service,
    step,
    days,
    salonCode,
    draft.employeeId,
    draft.noPreference,
  ]);

  useEffect(() => {
    if (!data || !service || !draft.dateKey || step !== 2) return;
    const key = availabilityCacheKey(
      draft.dateKey,
      draft.employeeId,
      draft.noPreference
    );
    const cached = availabilityCache.current.get(key);
    if (cached) {
      setAvailability(cached);
      setAvailabilityLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        setAvailabilityLoading(true);
        const result = await fetchDayAvailability(
          salonCode,
          draft.dateKey!,
          draft.noPreference ? null : draft.employeeId
        );
        if (cancelled) return;
        availabilityCache.current.set(key, result);
        setAvailability(result);
      } catch {
        if (!cancelled) setAvailability({ appointments: [], blocks: [] });
      } finally {
        if (!cancelled) setAvailabilityLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data, service, draft.dateKey, draft.employeeId, draft.noPreference, salonCode, step]);

  const slots = useMemo(() => {
    if (!data || !service || !draft.dateKey || !availability) return [];
    return buildSlots({
      data,
      service,
      dateKey: draft.dateKey,
      availability,
      selectedEmployee,
      noPreference: draft.noPreference,
    });
  }, [data, service, draft.dateKey, availability, selectedEmployee, draft.noPreference]);

  const availableSlots = slots.filter((s) => s.available);
  const slotsLoading =
    availabilityLoading || (!!draft.dateKey && availability === null);

  async function handleSubmit() {
    if (!service || !draft.slotStart || !draft.guestName.trim()) return;
    if (!draft.acceptConsent) {
      setError(t(locale, "consentRequired"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      if (draft.createAccount) {
        if (!draft.guestEmail) {
          setError(t(locale, "invalidEmail"));
          return;
        }
        const status = await validatePassword(auth, draft.accountPassword);
        if (!status.isValid) {
          setError(t(locale, "passwordTooWeak"));
          return;
        }
        await registerWebCustomer({
          salonCode,
          email: draft.guestEmail,
          password: draft.accountPassword,
          firstName: draft.guestName,
          phone: draft.guestPhone || null,
        });
      }

      const result = await submitWebBooking({
        salonCode,
        serviceId: service.id,
        employeeId: draft.noPreference ? null : draft.employeeId,
        noPreference: draft.noPreference,
        startAt: draft.slotStart.toISOString(),
        guestName: draft.guestName.trim(),
        guestPhone: draft.guestPhone.trim() || null,
        guestEmail: draft.guestEmail.trim() || null,
        note: draft.note.trim() || null,
        locale,
      });
      availabilityCache.current.clear();
      setAppointmentId(result.appointmentId);
      setStep(4);
    } catch (e) {
      setError(formatFirebaseError(e));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="page center">
        <p>{t(locale, "loading")}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="page center">
        <p className="error">{error ?? t(locale, "salonNotFound")}</p>
      </div>
    );
  }

  return (
    <div className="page">
      <header className="header">
        {data.branding.logoUrl ? (
          <img className="logo" src={data.branding.logoUrl} alt="" />
        ) : (
          <div className="logo" />
        )}
        <div>
          <h1 className="salon-name">{data.details.name}</h1>
          <p className="salon-meta">
            {[data.details.address, data.details.city].filter(Boolean).join(", ")}
          </p>
        </div>
      </header>

      {step < 4 && (
        <div className="steps">
          {[t(locale, "stepService"), t(locale, "stepStaff"), t(locale, "stepTime"), t(locale, "stepContact")].map(
            (label, index) => (
              <span
                key={label}
                className={`step-pill ${index === step ? "active" : ""} ${index < step ? "done" : ""}`}
              >
                {label}
              </span>
            )
          )}
        </div>
      )}

      <div className="card">
        {step === 0 && (
          <ServiceStep
            locale={locale}
            services={data.services}
            currency={data.settings.currency}
            selectedId={draft.serviceId}
            onSelect={(serviceId) => {
              setDraft({
                ...emptyDraft(),
                serviceId,
              });
              setStep(1);
            }}
          />
        )}

        {step === 1 && service && (
          <StaffStep
            locale={locale}
            employees={bookableEmployees}
            noPreference={draft.noPreference}
            selectedId={draft.employeeId}
            onSelectEmployee={(employeeId) => {
              setDraft((d) => ({
                ...d,
                employeeId,
                noPreference: false,
                dateKey: null,
                slotStart: null,
              }));
              setStep(2);
            }}
            onSelectNoPreference={() => {
              setDraft((d) => ({
                ...d,
                employeeId: null,
                noPreference: true,
                dateKey: null,
                slotStart: null,
              }));
              setStep(2);
            }}
            onBack={() => setStep(0)}
          />
        )}

        {step === 2 && service && (
          <TimeStep
            locale={locale}
            days={days}
            dateKey={draft.dateKey}
            slots={slots}
            availableCount={availableSlots.length}
            loading={slotsLoading}
            onSelectDay={(dateKey) => {
              setDraft((d) => ({ ...d, dateKey, slotStart: null }));
              const key = availabilityCacheKey(
                dateKey,
                draft.employeeId,
                draft.noPreference
              );
              const cached = availabilityCache.current.get(key);
              setAvailability(cached ?? null);
            }}
            onSelectSlot={(slotStart) => {
              setDraft((d) => ({ ...d, slotStart }));
              setStep(3);
            }}
            onBack={() => setStep(1)}
          />
        )}

        {step === 3 && service && draft.slotStart && (
          <ContactStep
            locale={locale}
            draft={draft}
            service={service}
            employee={selectedEmployee}
            noPreference={draft.noPreference}
            currency={data.settings.currency}
            slotStart={draft.slotStart}
            submitting={submitting}
            error={error}
            onChange={(patch) => setDraft((d) => ({ ...d, ...patch }))}
            onBack={() => setStep(2)}
            onSubmit={handleSubmit}
          />
        )}

        {step === 4 && service && draft.slotStart && (
          <SuccessStep
            locale={locale}
            service={service}
            employee={selectedEmployee}
            noPreference={draft.noPreference}
            slotStart={draft.slotStart}
            email={draft.guestEmail}
          />
        )}
      </div>

      <p className="footer">
        {t(locale, "poweredBy")} WERKSTATE
        {appointmentId ? ` · #${appointmentId.slice(0, 6)}` : ""}
      </p>
      <LegalFooter locale={locale} />
    </div>
  );
}

function ServiceStep({
  locale,
  services,
  currency,
  selectedId,
  onSelect,
}: {
  locale: Locale;
  services: BookingService[];
  currency: string;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <>
      <h2>{t(locale, "chooseService")}</h2>
      <div className="option-grid">
        {services.map((service) => (
          <button
            key={service.id}
            type="button"
            className={`option ${selectedId === service.id ? "selected" : ""}`}
            onClick={() => onSelect(service.id)}
          >
            <div>
              <div className="option-title">{service.name}</div>
              <div className="option-sub">
                {t(locale, "duration")}: {service.durationMinutes} {t(locale, "minutes")}
              </div>
            </div>
            {formatPrice(service.priceCents, currency, locale) && (
              <div className="option-title">
                {formatPrice(service.priceCents, currency, locale)}
              </div>
            )}
          </button>
        ))}
      </div>
    </>
  );
}

function StaffStep({
  locale,
  employees,
  noPreference,
  selectedId,
  onSelectEmployee,
  onSelectNoPreference,
  onBack,
}: {
  locale: Locale;
  employees: BookingEmployee[];
  noPreference: boolean;
  selectedId: string | null;
  onSelectEmployee: (id: string) => void;
  onSelectNoPreference: () => void;
  onBack: () => void;
}) {
  return (
    <>
      <h2>{t(locale, "chooseStaff")}</h2>
      <div className="option-grid">
        <button
          type="button"
          className={`option ${noPreference ? "selected" : ""}`}
          onClick={onSelectNoPreference}
        >
          <div className="option-title">{t(locale, "noPreference")}</div>
        </button>
        {employees.map((employee) => (
          <button
            key={employee.id}
            type="button"
            className={`option ${selectedId === employee.id ? "selected" : ""}`}
            onClick={() => onSelectEmployee(employee.id)}
          >
            <div className="option-title">
              {[employee.firstName, employee.lastName].filter(Boolean).join(" ")}
            </div>
          </button>
        ))}
      </div>
      <div className="actions">
        <button type="button" className="btn btn-secondary" onClick={onBack}>
          {t(locale, "back")}
        </button>
      </div>
    </>
  );
}

function TimeStep({
  locale,
  days,
  dateKey,
  slots,
  availableCount,
  loading,
  onSelectDay,
  onSelectSlot,
  onBack,
}: {
  locale: Locale;
  days: string[];
  dateKey: string | null;
  slots: ReturnType<typeof buildSlots>;
  availableCount: number;
  loading: boolean;
  onSelectDay: (dateKey: string) => void;
  onSelectSlot: (slotStart: Date) => void;
  onBack: () => void;
}) {
  return (
    <>
      <h2>{t(locale, "chooseDay")}</h2>
      <div className="day-row">
        {days.map((day) => (
          <button
            key={day}
            type="button"
            className={`chip ${dateKey === day ? "selected" : ""}`}
            onClick={() => onSelectDay(day)}
          >
            {new Intl.DateTimeFormat(locale, {
              weekday: "short",
              day: "numeric",
              month: "short",
              timeZone: "Europe/Berlin",
            }).format(berlinLocalDate(day))}
          </button>
        ))}
      </div>

      {dateKey && (
        <>
          <h2 style={{ marginTop: 20 }}>{t(locale, "chooseSlot")}</h2>
          {loading ? (
            <p className="muted">{t(locale, "loading")}</p>
          ) : availableCount === 0 ? (
            <p className="muted">{t(locale, "noSlots")}</p>
          ) : (
            <div className="slot-row">
              {slots
                .filter((slot) => slot.available)
                .map((slot) => (
                  <button
                    key={slot.startAt.toISOString()}
                    type="button"
                    className="chip"
                    onClick={() => onSelectSlot(slot.startAt)}
                  >
                    {formatSlotTime(slot.startAt, locale)}
                  </button>
                ))}
            </div>
          )}
        </>
      )}

      <div className="actions">
        <button type="button" className="btn btn-secondary" onClick={onBack}>
          {t(locale, "back")}
        </button>
      </div>
    </>
  );
}

function berlinLocalDate(dateKey: string): Date {
  const [y, m, d] = dateKey.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
}

function ContactStep({
  locale,
  draft,
  service,
  employee,
  noPreference,
  currency,
  slotStart,
  submitting,
  error,
  onChange,
  onBack,
  onSubmit,
}: {
  locale: Locale;
  draft: BookingDraft;
  service: BookingService;
  employee: BookingEmployee | null;
  noPreference: boolean;
  currency: string;
  slotStart: Date;
  submitting: boolean;
  error: string | null;
  onChange: (patch: Partial<BookingDraft>) => void;
  onBack: () => void;
  onSubmit: () => void;
}) {
  const staffName = noPreference
    ? t(locale, "noPreference")
    : employee
      ? [employee.firstName, employee.lastName].filter(Boolean).join(" ")
      : "";

  return (
    <>
      <div className="summary">
        <p>
          <strong>{service.name}</strong>
        </p>
        <p>{formatSlotDate(slotStart, locale)}</p>
        <p>{formatSlotTime(slotStart, locale)}</p>
        {staffName && <p>{staffName}</p>}
        {formatPrice(service.priceCents, currency, locale) && (
          <p>{formatPrice(service.priceCents, currency, locale)}</p>
        )}
      </div>

      <h2>{t(locale, "yourDetails")}</h2>
      <div className="form-grid">
        <label>
          {t(locale, "name")} *
          <input
            value={draft.guestName}
            onChange={(e) => onChange({ guestName: e.target.value })}
          />
        </label>
        <label>
          {t(locale, "phone")}
          <input
            value={draft.guestPhone}
            onChange={(e) => onChange({ guestPhone: e.target.value })}
            type="tel"
          />
        </label>
        <label>
          {t(locale, "email")}
          <span className="muted">{t(locale, "emailHint")}</span>
          <input
            value={draft.guestEmail}
            onChange={(e) => onChange({ guestEmail: e.target.value })}
            type="email"
          />
        </label>
        <label>
          {t(locale, "note")}
          <textarea value={draft.note} onChange={(e) => onChange({ note: e.target.value })} />
        </label>
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={draft.createAccount}
            onChange={(e) => onChange({ createAccount: e.target.checked })}
          />
          <span>{t(locale, "createAccount")}</span>
        </label>
        {draft.createAccount && (
          <label>
            {t(locale, "password")}
            <input
              type="password"
              value={draft.accountPassword}
              onChange={(e) => onChange({ accountPassword: e.target.value })}
              minLength={10}
              autoComplete="new-password"
            />
            <span className="muted">{t(locale, "passwordMin")}</span>
          </label>
        )}
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={draft.acceptConsent}
            onChange={(e) => onChange({ acceptConsent: e.target.checked })}
            required
          />
          <span>
            {t(locale, "consentPrefix")}
            <a
              href={`https://werkstate.app/privacy.html?lang=${locale}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t(locale, "consentPrivacyLink")}
            </a>
            {t(locale, "consentAnd")}
            <a
              href={`https://werkstate.app/terms.html?lang=${locale}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t(locale, "consentTermsLink")}
            </a>
          </span>
        </label>
      </div>

      {error && <p className="error">{error}</p>}

      <div className="actions">
        <button type="button" className="btn btn-secondary" onClick={onBack} disabled={submitting}>
          {t(locale, "back")}
        </button>
        <button
          type="button"
          className={`btn btn-primary${submitting ? " loading" : ""}`}
          onClick={onSubmit}
          disabled={submitting || !draft.guestName.trim() || !draft.acceptConsent}
        >
          {submitting ? t(locale, "booking") : t(locale, "confirmBooking")}
        </button>
      </div>
    </>
  );
}

function SuccessStep({
  locale,
  service,
  employee,
  noPreference,
  slotStart,
  email,
}: {
  locale: Locale;
  service: BookingService;
  employee: BookingEmployee | null;
  noPreference: boolean;
  slotStart: Date;
  email: string;
}) {
  const staffName = noPreference
    ? t(locale, "noPreference")
    : employee
      ? [employee.firstName, employee.lastName].filter(Boolean).join(" ")
      : "";

  return (
    <div className="center">
      <div className="success-icon">✓</div>
      <h2>{t(locale, "successTitle")}</h2>
      <p>{t(locale, "successBody")}</p>
      <div className="summary" style={{ textAlign: "left", marginTop: 16 }}>
        <p>
          <strong>{service.name}</strong>
        </p>
        <p>{formatSlotDate(slotStart, locale)}</p>
        <p>{formatSlotTime(slotStart, locale)}</p>
        {staffName && <p>{staffName}</p>}
      </div>
      {email && <p className="muted">{t(locale, "successEmail")}</p>}
    </div>
  );
}
